import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Typography } from '@mui/material'

import { useInternalAccessCheck } from '../../features/account/hooks/roleHooks'
import { GRTooltip } from '../GRTooltip/GRTooltip'
import {
  INTERNAL_ONLY_STRIP_CATEGORIES,
  LiveEventStripCategory,
  STRIP_CATEGORY_COLOR,
  STRIP_CATEGORY_DESCRIPTION_KEY,
  STRIP_CATEGORY_LABEL_KEY,
  STRIP_CATEGORY_ORDER,
} from './eventCategories'

type NewEventsStripCategoryTogglesProps = {
  activeCategories: LiveEventStripCategory[]
  onChange: (categories: LiveEventStripCategory[]) => void
}

export const NewEventsStripCategoryToggles: FC<NewEventsStripCategoryTogglesProps> = ({ activeCategories, onChange }) => {
  const { t } = useTranslation()
  const internalUser = useInternalAccessCheck()

  const visibleCategories = STRIP_CATEGORY_ORDER.filter((category) => internalUser || !INTERNAL_ONLY_STRIP_CATEGORIES.includes(category))

  const toggleCategory = (category: LiveEventStripCategory) => {
    onChange(activeCategories.includes(category) ? activeCategories.filter((c) => c !== category) : [...activeCategories, category])
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-end' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center' }}>
        {t('live-events:new_events_strip_show_label')}
      </Typography>
      {visibleCategories.map((category) => {
        const active = activeCategories.includes(category)
        const color = STRIP_CATEGORY_COLOR[category]
        const inactiveOutlineSx = { backgroundColor: 'transparent', color: `${color}.main`, borderColor: `${color}.main` }

        return (
          <GRTooltip key={category} content={t(STRIP_CATEGORY_DESCRIPTION_KEY[category])}>
            <Chip
              label={t(STRIP_CATEGORY_LABEL_KEY[category])}
              size="small"
              color={color}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => toggleCategory(category)}
              // m: 0 — spacing is via the container gap, not the theme's default chip margin.
              sx={{ cursor: 'pointer', m: 0, ...(!active && inactiveOutlineSx) }}
            />
          </GRTooltip>
        )
      })}
    </Box>
  )
}
